'use client';

import { c as _c } from "react/compiler-runtime";
import { Collapse } from '@/uiComposites/interactive/Collapse';
import { useAccordionItemOpened, useDispatchAccordion } from '@/uiComposites/interactive/accordion/AccordionProvider.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { ReactNode, useId } from 'react';
import { stationShelf, stationOccupy, contentSegment, stationOpened, station, stationClosed } from './StepByStep.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { box } from '@/uiPrimitives/layout/box';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { icon } from '@/uiPrimitives/media/icon';
import { accordionItem, delayIconTransitionHover } from '@/uiComposites/interactive/accordion/Accordion.css';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { arrange } from '@/uiPrimitives/layout/arrange';
import { translateInline } from '@/globals/translate/translateInline';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { A11YAccordionActionLabel } from '@/uiComposites/interactive/accordion/helpers/A11YAccordionActionLabel';
import { textTag } from '@/uiComposites/typography/textTag';
import { slugify } from '@/globals/helpers/slugify.helpers';
import { CopyAnchor } from '@/sections/copyAnchor/CopyAnchor';
import { copyAnchorParentCSS } from '@/sections/copyAnchor/CopyAnchor.css';
export const getStepID = (title: string) => {
  return `step:${slugify(title)}`;
};
export const StepByStepItem = t0 => {
  const $ = _c(71);
  const {
    title,
    children,
    stepMarker,
    mandatory,
    index
  } = t0;
  const id = useId();
  const isOpened = useAccordionItemOpened(index);
  const dispatchAccordion = useDispatchAccordion();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(contentSegment, stack({}));
    t2 = classes(textStyle({
      color: "positiveBlue",
      capSize: "1 | h3"
    }));
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = getStepID(title);
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== dispatchAccordion || $[5] !== index || $[6] !== isOpened) {
    t4 = event => {
      if (event.target !== event.currentTarget) {
        return;
      }
      event.currentTarget.querySelector("button")?.focus({
        preventScroll: true
      });
      if (isOpened) {
        return;
      }
      dispatchAccordion({
        type: "open",
        id: index
      });
    };
    $[4] = dispatchAccordion;
    $[5] = index;
    $[6] = isOpened;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classes(stationShelf);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== dispatchAccordion || $[10] !== index || $[11] !== isOpened) {
    t6 = () => {
      dispatchAccordion({
        type: isOpened ? "close" : "open",
        id: index
      });
    };
    $[9] = dispatchAccordion;
    $[10] = index;
    $[11] = isOpened;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = addTestSelector("uiStepByStepItem");
    t8 = classes(stationOccupy);
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  const t9 = isOpened ? stationOpened : stationClosed;
  let t10;
  if ($[15] !== t9) {
    t10 = classes(station.main, t9);
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== stepMarker || $[18] !== t10) {
    t11 = <div className={t8}><div className={t10}>{stepMarker}</div></div>;
    $[17] = stepMarker;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classes(shelf({
      alignItems: "baseline",
      gap: "0 | p",
      gridTemplateColumns: "minmax(0, 1fr) auto"
    }));
    t13 = classes(arrange({
      layout: {
        max640px: "stack",
        min640px: "shelf"
      },
      rowGap: "2 | h2",
      columnGap: "0 | p",
      gridTemplateColumns: {
        min640px: "minmax(0, 1fr) auto"
      },
      alignItems: "start"
    }));
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  let t14;
  if ($[22] !== isOpened) {
    t14 = classes(textBlock({
      fontWeight: "bold",
      textWrap: "balance",
      color: isOpened ? "positive" : undefined
    }));
    $[22] = isOpened;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== title) {
    t15 = getStepID(title);
    $[24] = title;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = classes(box({
      paddingInlineEnd: "4 | betweenListItems"
    }));
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t15) {
    t17 = <CopyAnchor id={t15} className={t16} />;
    $[27] = t15;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17 || $[30] !== title) {
    t18 = <span className={copyAnchorParentCSS}>{title}{t17}</span>;
    $[29] = t17;
    $[30] = title;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== isOpened) {
    t19 = <A11YAccordionActionLabel isOpened={isOpened} />;
    $[32] = isOpened;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t14 || $[35] !== t18 || $[36] !== t19) {
    t20 = <div className={t14}>{t18}{t19}</div>;
    $[34] = t14;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== mandatory) {
    t21 = mandatory === true && <div className={classes(occupy({
      blockSize: {
        max640px: "auto",
        min640px: "capSize"
      }
    }))} {...addTestSelector("uiMandatory")}><div className={classes(textTag())}>{translateInline({
          en: "Mandatory",
          nl: "Verplicht"
        })}</div></div>;
    $[38] = mandatory;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className={t13}>{t20}{t21}</div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== isOpened) {
    t23 = classes(delayIconTransitionHover, icon({
      icon: chevronIcon,
      adjustIcon: "rotate90deg",
      flip: isOpened ? "horizontal:hover" : "horizontalNone:hover",
      blockSize: "1ex"
    }));
    $[43] = isOpened;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t23) {
    t24 = <i className={t23} />;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t22 || $[48] !== t24) {
    t25 = <div className={t12}>{t22}{t24}</div>;
    $[47] = t22;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== id || $[51] !== isOpened || $[52] !== t11 || $[53] !== t25 || $[54] !== t6) {
    t26 = <button aria-controls={id} aria-expanded={isOpened} className={t5} tabIndex={0} onClick={t6} {...t7}>{t11}{t25}</button>;
    $[50] = id;
    $[51] = isOpened;
    $[52] = t11;
    $[53] = t25;
    $[54] = t6;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t26 || $[57] !== t3 || $[58] !== t4) {
    t27 = <h2 className={t2} tabIndex={-1} id={t3} onFocus={t4}>{t26}</h2>;
    $[56] = t26;
    $[57] = t3;
    $[58] = t4;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  let t29;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = classes(accordionItem, box({
      paddingBlockStart: "5 | betweenParagraphs",
      paddingBlockEnd: "negative 0 | p"
    }));
    t29 = addTestSelector("uiAccordionContent");
    $[60] = t28;
    $[61] = t29;
  } else {
    t28 = $[60];
    t29 = $[61];
  }
  let t30;
  if ($[62] !== children) {
    t30 = <div className={t28} {...t29}>{children}</div>;
    $[62] = children;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== id || $[65] !== isOpened || $[66] !== t30) {
    t31 = <Collapse id={id} isOpened={isOpened}>{t30}</Collapse>;
    $[64] = id;
    $[65] = isOpened;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t27 || $[69] !== t31) {
    t32 = <div className={t1}>{t27}{t31}</div>;
    $[68] = t27;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  return t32;
};